// ============================================
// 3D 展示区域（2026-10-06）：左键框选「有限展示区域」
// - 世界坐标 XZ 平面矩形（类似 Excel 打印区域）；渲染时四壁裁剪，仅区域内可见
// - 编辑/预览/发布共用（scene.config.displayRegion 持久化）
// ============================================

export interface DisplayRegion {
  /** AABB（兼容旧数据/相机限制/尺寸显示用） */
  x1: number;
  z1: number;
  x2: number;
  z2: number;
  /** 2026-10-06：框选四角投影地面四边形（顺序 左上→右上→右下→左下）；有则裁剪按四边形（贴合所见） */
  quad?: { x: number; z: number }[];
}

/** 最小区域边长（米）；小于则视为误触不生效 */
export const MIN_REGION_SIZE = 2;

/** 两点拖拽 → 规范化矩形（排序 + 最小尺寸校验；过小返回 null=取消） */
export function normalizeRegion(ax: number, az: number, bx: number, bz: number): DisplayRegion | null {
  const x1 = Math.min(ax, bx);
  const x2 = Math.max(ax, bx);
  const z1 = Math.min(az, bz);
  const z2 = Math.max(az, bz);
  if (![x1, x2, z1, z2].every((v) => Number.isFinite(v))) return null;
  if (x2 - x1 < MIN_REGION_SIZE || z2 - z1 < MIN_REGION_SIZE) return null;
  return { x1, z1, x2, z2 };
}

export interface PlaneDef {
  nx: number;
  ny: number;
  nz: number;
  /** THREE.Plane constant：normal·p + constant >= 0 保留 */
  constant: number;
}

/** 四角投影点（屏幕矩形四角投到地面的四边形）→ 区域（AABB + quad）；退化（<2m / 非凸）返回 null */
export function regionFromQuad(pts: { x: number; z: number }[]): DisplayRegion | null {
  if (pts.length !== 4 || !pts.every((p) => Number.isFinite(p.x) && Number.isFinite(p.z))) return null;
  const xs = pts.map((p) => p.x);
  const zs = pts.map((p) => p.z);
  const x1 = Math.min(...xs), x2 = Math.max(...xs);
  const z1 = Math.min(...zs), z2 = Math.max(...zs);
  if (x2 - x1 < MIN_REGION_SIZE || z2 - z1 < MIN_REGION_SIZE) return null;
  // 凸性检查（叉积同号；允许轻微数值误差）
  const cross: number[] = [];
  for (let i = 0; i < 4; i++) {
    const a = pts[i], b = pts[(i + 1) % 4], c = pts[(i + 2) % 4];
    cross.push((b.x - a.x) * (c.z - a.z) - (b.z - a.z) * (c.x - a.x));
  }
  const hasPos = cross.some((v) => v > 1e-6);
  const hasNeg = cross.some((v) => v < -1e-6);
  if (hasPos && hasNeg) return null; // 自交/凹 → 回退
  return { x1, z1, x2, z2, quad: pts.map((p) => ({ x: p.x, z: p.z })) };
}

/** 四壁裁剪平面（法线朝内，保留区域内一侧）；有 quad 时按四边形四边（贴合所见） */
export function regionPlaneDefs(region: DisplayRegion): PlaneDef[] {
  const q = region.quad;
  if (q && q.length === 4) {
    const cx = q.reduce((s, p) => s + p.x, 0) / 4;
    const cz = q.reduce((s, p) => s + p.z, 0) / 4;
    const out: PlaneDef[] = [];
    for (let i = 0; i < 4; i++) {
      const a = q[i];
      const b = q[(i + 1) % 4];
      const dx = b.x - a.x;
      const dz = b.z - a.z;
      const len = Math.hypot(dx, dz) || 1;
      let nx = dz / len;
      let nz = -dx / len;
      // 中心点须在保留侧：normal·(centroid - a) >= 0，否则翻转
      if (nx * (cx - a.x) + nz * (cz - a.z) < 0) {
        nx = -nx;
        nz = -nz;
      }
      out.push({ nx, ny: 0, nz, constant: -(nx * a.x + nz * a.z) });
    }
    return out;
  }
  return [
    { nx: 1, ny: 0, nz: 0, constant: -region.x1 },
    { nx: -1, ny: 0, nz: 0, constant: region.x2 },
    { nx: 0, ny: 0, nz: 1, constant: -region.z1 },
    { nx: 0, ny: 0, nz: -1, constant: region.z2 },
  ];
}

/** 相机目标点限制在区域内（margin 内缩，便于贴边观察） */
export function clampTargetToRegion(x: number, z: number, region: DisplayRegion, margin = 0): { x: number; z: number } {
  const lo = margin, hi = -margin;
  return {
    x: Math.min(Math.max(x, region.x1 + lo), region.x2 + hi),
    z: Math.min(Math.max(z, region.z1 + lo), region.z2 + hi),
  };
}

/** 矩形四角（y 高度）→ 线框点序列（含闭合）；有 quad 时按四边形 */
export function regionLinePoints(region: DisplayRegion, y = 0.4): [number, number, number][] {
  if (region.quad && region.quad.length === 4) {
    const pts = region.quad.map((p) => [p.x, y, p.z] as [number, number, number]);
    return [...pts, pts[0]];
  }
  return [
    [region.x1, y, region.z1],
    [region.x2, y, region.z1],
    [region.x2, y, region.z2],
    [region.x1, y, region.z2],
    [region.x1, y, region.z1],
  ];
}

export function formatRegion(region: DisplayRegion): string {
  return `${Math.round(region.x2 - region.x1)} × ${Math.round(region.z2 - region.z1)} m`;
}
